import { FC } from "react";
import { registerDataType } from "../RegisterForm";
import { Button, Flex, Form, Input, Tooltip } from "antd";
import AuthTitleAndDescription from "../../AuthTitleAndDescription";
import { useIntl } from "react-intl";

interface NameAndPasswordStepProps {
  registerData: registerDataType;
  nextStep: () => void;
  addRegisterData: (data: Partial<registerDataType>) => void;
}
const infoSVG = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="15"
    viewBox="0 0 16 15"
    fill="none"
  >
    <path
      d="M8 0C6.51664 0 5.0666 0.439867 3.83323 1.26398C2.59986 2.08809 1.63856 3.25943 1.07091 4.62987C0.50325 6.00032 0.354725 7.50832 0.644114 8.96318C0.933503 10.418 1.64781 11.7544 2.6967 12.8033C3.7456 13.8522 5.08197 14.5665 6.53683 14.8559C7.99168 15.1453 9.49968 14.9968 10.8701 14.4291C12.2406 13.8614 13.4119 12.9001 14.236 11.6668C15.0601 10.4334 15.5 8.98336 15.5 7.5C15.5 5.51088 14.7098 3.60322 13.3033 2.1967C11.8968 0.790176 9.98913 0 8 0ZM8 13.75C6.76387 13.75 5.5555 13.3834 4.52769 12.6967C3.49988 12.0099 2.6988 11.0338 2.22576 9.89177C1.75271 8.74973 1.62894 7.49307 1.8701 6.28069C2.11125 5.0683 2.70651 3.95466 3.58059 3.08058C4.45466 2.2065 5.56831 1.61125 6.78069 1.37009C7.99307 1.12893 9.24974 1.25271 10.3918 1.72575C11.5338 2.1988 12.5099 2.99988 13.1967 4.02769C13.8834 5.05549 14.25 6.26387 14.25 7.5C14.25 9.1576 13.5915 10.7473 12.4194 11.9194C11.2473 13.0915 9.65761 13.75 8 13.75Z"
      fill="#9999A7"
    />
    <path
      d="M7.99981 6.06208C7.83405 6.06208 7.67508 6.12793 7.55787 6.24514C7.44066 6.36235 7.37481 6.52132 7.37481 6.68708V10.9371C7.37481 11.1028 7.44066 11.2618 7.55787 11.379C7.67508 11.4962 7.83405 11.5621 7.99981 11.5621C8.16557 11.5621 8.32454 11.4962 8.44175 11.379C8.55896 11.2618 8.62481 11.1028 8.62481 10.9371V6.68708C8.62481 6.52132 8.55896 6.36235 8.44175 6.24514C8.32454 6.12793 8.16557 6.06208 7.99981 6.06208ZM7.99981 3.49958C7.83061 3.50594 7.67027 3.57684 7.55172 3.69772C7.43316 3.8186 7.36538 3.98029 7.36231 4.14958V4.24958C7.36201 4.33112 7.3787 4.41184 7.4113 4.48658C7.4439 4.56133 7.4917 4.62847 7.55167 4.68373C7.61163 4.73899 7.68245 4.78117 7.7596 4.80757C7.83675 4.83397 7.91856 4.84402 7.99981 4.83708C8.16359 4.83089 8.319 4.76306 8.43489 4.64716C8.55079 4.53126 8.61862 4.37586 8.62481 4.21208V4.06208C8.6253 3.98264 8.60896 3.904 8.57687 3.83134C8.54478 3.75867 8.49766 3.69363 8.43861 3.64049C8.37957 3.58735 8.30994 3.54732 8.2343 3.52303C8.15867 3.49874 8.07875 3.49075 7.99981 3.49958Z"
      fill="#9999A7"
    />
  </svg>
);
const NameAndPasswordStep: FC<NameAndPasswordStepProps> = ({
  nextStep,
  addRegisterData,
  registerData,
}) => {
  const intl = useIntl();
  const onFinish = (values: Partial<registerDataType>) => {
    addRegisterData(values);
    nextStep();
  };
  const [form] = Form.useForm<Partial<registerDataType>>();

  return (
    <div className="space-y-4">
      <AuthTitleAndDescription
        titleAndDescription={{
          title: intl.formatMessage({
            id: "welcomeLookfindr",
          }),
          description: intl.formatMessage({
            id: "tellAboutYourself",
          }),
        }}
      />
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        // className="space-y-2"
        initialValues={registerData}
      >
        <Flex justify="space-between" align="center" gap="15px">
          <Form.Item
            className="w-full"
            name={"firstName"}
            label={intl.formatMessage({ id: "firstName" })}
            colon={false}
            required={false}
            hasFeedback
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: "firstNameRequired" }),
              },
              {
                min: 3,
                message: intl.formatMessage({ id: "nameLt3Chr" }),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            className="w-full"
            name={"lastName"}
            label={intl.formatMessage({ id: "lastName" })}
            colon={false}
            required={false}
            hasFeedback
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: "lastNameRequired" }),
              },
              {
                min: 3,
                message: intl.formatMessage({ id: "nameLt3Chr" }),
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Flex>

        <Form.Item
          name={"password"}
          label={
            <div className=" w-full flex justify-between items-center gap-20">
              <span className="inline-block md:hidden">
                <Tooltip
                  title={intl.formatMessage({ id: "passwordLength8" })}
                  trigger={"hover"}
                >
                  <span className="">
                    {intl.formatMessage({ id: "password" })}
                  </span>
                  <span className="mx-1">{infoSVG}</span>
                </Tooltip>
              </span>
              <span className="hidden md:inline-block">
                {intl.formatMessage({ id: "password" })}
              </span>
              <span className="hidden md:inline-block text-gray-500 text-sm justify-self-end ">
                {intl.formatMessage({ id: "passwordLength8" })}
              </span>
            </div>
          }
          colon={false}
          required={false}
          hasFeedback
          rules={[
            {
              required: true,
              message: intl.formatMessage({ id: "passwordRequired" }),
            },
            {
              min: 8,
              message: intl.formatMessage({ id: "passwordLength8" }),
            },
          ]}
          // tooltip={<small> يجب ان تتكون من 8 احرف على الاقل</small>}
        >
          <Input.Password />
        </Form.Item>

        <Form.Item className="">
          <Button type="primary" htmlType="submit" className="w-full my-2">
            {intl.formatMessage({ id: "continue" })}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default NameAndPasswordStep;
